import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Snackbar,
  Alert,
  Slide,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { BORDER_RADIUS, ANIMATION } from "../../theme/constants";

/**
 * Snackbar Context for app-wide notifications
 * Supports configurable position, severity, duration, and custom actions
 */
const SnackbarContext = createContext(null);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

const severityIcons = {
  success: <SuccessIcon fontSize="small" />,
  error: <ErrorIcon fontSize="small" />,
  warning: <WarningIcon fontSize="small" />,
  info: <InfoIcon fontSize="small" />,
};

function SlideTransition(props) {
  const { anchorOrigin } = props;
  const direction = anchorOrigin?.vertical === "top" ? "down" : "up";
  return <Slide {...props} direction={direction} />;
}

export function SnackbarProvider({ children, defaultPosition }) {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    duration: 4000,
    position: defaultPosition || { vertical: "bottom", horizontal: "right" },
    action: null,
  });

  const showSnackbar = useCallback(
    (message, options = {}) => {
      setSnackbar({
        open: true,
        message,
        severity: options.severity || "info",
        duration: options.duration !== undefined ? options.duration : 4000,
        position: options.position ||
          defaultPosition || { vertical: "bottom", horizontal: "right" },
        action: options.action || null,
      });
    },
    [defaultPosition],
  );

  const notify = useMemo(
    () => ({
      success: (message, options) =>
        showSnackbar(message, { severity: "success", ...options }),
      error: (message, options) =>
        showSnackbar(message, { severity: "error", ...options }),
      warning: (message, options) =>
        showSnackbar(message, { severity: "warning", ...options }),
      info: (message, options) =>
        showSnackbar(message, { severity: "info", ...options }),
    }),
    [showSnackbar],
  );

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const contextValue = {
    showSnackbar,
    hideSnackbar,
    notify,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={hideSnackbar}
        anchorOrigin={snackbar.position}
        TransitionComponent={(props) => (
          <SlideTransition {...props} anchorOrigin={snackbar.position} />
        )}
        sx={{
          "& .MuiSnackbarContent-root": {
            borderRadius: BORDER_RADIUS.md,
          },
        }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          variant="filled"
          icon={severityIcons[snackbar.severity]}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {snackbar.action && snackbar.action}
              <IconButton
                size="small"
                color="inherit"
                onClick={hideSnackbar}
                sx={{ ml: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{
            width: "100%",
            minWidth: 280,
            maxWidth: 440,
            borderRadius: `${BORDER_RADIUS.md}px`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            alignItems: "center",
            "& .MuiAlert-message": {
              padding: "2px 0",
              fontSize: "0.8125rem",
              fontWeight: 500,
            },
            "& .MuiAlert-icon": {
              padding: "2px 0",
              marginRight: 1,
            },
            "& .MuiAlert-action": {
              padding: "0 0 0 8px",
              marginRight: 0,
            },
            animation: `fadeIn ${ANIMATION.fast}ms ease-in-out`,
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "scale(0.95)" },
              to: { opacity: 1, transform: "scale(1)" },
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {snackbar.message}
          </Typography>
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export default SnackbarProvider;
